import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Eye, EyeOff, Loader2, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod/v3';
import { AnimatedCubeIcon } from '@/components/animated/animated-cube-icon';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { FormError } from '@/components/ui/form-error';
import { useAuth } from '@/context/auth-context';
import { api, isApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

const setPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'A senha deve ter no mínimo 8 caracteres')
      .max(32, 'A senha deve ter no máximo 32 caracteres')
      .regex(/[A-Z]/, 'A senha deve conter ao menos uma letra maiúscula')
      .regex(/[a-z]/, 'A senha deve conter ao menos uma letra minúscula')
      .regex(/[0-9]/, 'A senha deve conter ao menos um número')
      .regex(
        /[!@#$%^&*()_\-+=[\]{};:'"\\|,.<>/?`~]/,
        'A senha deve conter ao menos um caractere especial',
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });

type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

export function SetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout, user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const userId = searchParams.get('id') || user?.id;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SetPasswordFormData>({
    mode: 'onChange',
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password') || '';

  const rules = useMemo(
    () => [
      {
        label: 'A senha deve ter no mínimo 8 caracteres.',
        isValid: password.length >= 8,
      },
      {
        label: 'A senha deve ter no máximo 32 caracteres.',
        isValid: password.length >= 8 && password.length <= 32,
      },
      {
        label: 'A senha deve conter uma letra minúscula.',
        isValid: /[a-z]/.test(password),
      },
      {
        label: 'A senha deve conter uma letra maiúscula.',
        isValid: /[A-Z]/.test(password),
      },
      {
        label: 'A senha deve conter ao menos um número.',
        isValid: /[0-9]/.test(password),
      },
      {
        label: 'A senha deve ter ao menos um caractere especial.',
        isValid: /[!@#$%^&*()_\-+=[\]{};:'"\\|,.<>/?`~]/.test(password),
      },
    ],
    [password],
  );

  const onSubmit = async (data: SetPasswordFormData) => {
    if (!userId) {
      setError('ID de usuário não encontrado');
      return;
    }

    try {
      setError(null);

      await api.put(`/admin/users/${userId}/password`, {
        password: data.password,
      });

      await logout();
      navigate('/login');
    } catch (err) {
      if (isApiError(err)) {
        setError(err.response?.data.message || 'Erro ao alterar senha');
      } else {
        setError('Erro ao alterar senha. Tente novamente.');
      }
    }
  };

  const avatarUrl = user?.avatar;

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8 bg-gray-50">
      <div className="w-full max-w-6xl h-[40rem] lg:h-[50rem] max-h-[90vh] p-20 rounded-xl border border-gray-200 bg-white text-gray-900 shadow">
        <div className="flex flex-col lg:flex-row">
          <div className="hidden lg:flex flex-1 items-center justify-center relative">
            <div
              className={cn(
                'absolute inset-0 rounded-l-xl',
                '[background-size:40px_40px]',
                '[background-image:linear-gradient(to_right,#d4d4d8_1px,transparent_1px),linear-gradient(to_bottom,#d4d4d8_1px,transparent_1px)]',
              )}
            />

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] rounded-l-xl" />

            <div className="relative text-center">
              <AnimatedCubeIcon size={60} className="mb-8 mx-auto" />
              <p className="bg-gradient-to-b from-gray-300 to-black bg-clip-text text-4xl font-bold text-transparent lg:text-5xl">
                BCUBE
              </p>
              <p className="mt-4 text-gray-600">Recuperação de senha</p>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-md">
              <div className="text-center px-0">
                <div className="lg:hidden mb-6 flex justify-center">
                  <AnimatedCubeIcon size={40} />
                </div>

                <div className="mb-6 flex justify-center">
                  <Avatar className="h-24 w-24 lg:h-20 lg:w-20">
                    <AvatarImage
                      src={avatarUrl ?? '/avatar.webp'}
                      alt={
                        avatarUrl
                          ? 'Your user profile picture'
                          : 'Avatar do Oswaldo Conti Bosso dando um tchau com a mão direita'
                      }
                    />
                  </Avatar>
                </div>

                <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                  Crie sua nova senha
                </h1>
              </div>

              <div className="px-0 mt-4">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="grid grid-cols-1 gap-x-6 gap-y-8">
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Senha
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...register('password')}
                          className={cn(
                            'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 pr-10 text-sm shadow-sm transition-colors',
                            'placeholder:text-gray-400',
                            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            errors.password ? 'border-red-500' : 'border-gray-300',
                          )}
                        />
                        <button
                          type="button"
                          aria-label="Alternar visibilidade da senha"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <section aria-label="Regras de senha" className="space-y-1 text-sm">
                      {rules.map((rule) => (
                        <p
                          key={rule.label}
                          className={cn(
                            'flex items-center gap-2',
                            rule.isValid ? 'text-green-600' : 'text-red-600',
                          )}
                        >
                          {rule.isValid ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          {rule.label}
                        </p>
                      ))}
                    </section>

                    <div>
                      <label
                        htmlFor="confirmPassword"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Confirme sua senha
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...register('confirmPassword')}
                          className={cn(
                            'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 pr-10 text-sm shadow-sm transition-colors',
                            'placeholder:text-gray-400',
                            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            errors.confirmPassword ? 'border-red-500' : 'border-gray-300',
                          )}
                        />
                        <button
                          type="button"
                          aria-label="Alternar visibilidade da confirmação"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.confirmPassword.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <FormError error={error} onDismiss={() => setError(null)} />
                  <div className="mt-6 flex items-center justify-end gap-x-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={cn(
                        'inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium',
                        'bg-gray-900 text-white shadow',
                        'hover:bg-gray-800',
                        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400',
                        'disabled:pointer-events-none disabled:opacity-50',
                      )}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        'Salvar'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
